/* eslint-disable no-undef */

/**
 * WebReaderService
 *
 * Centralizes all ReadSpeaker WebReader interactions.
 *
 * WHY LANGUAGE SYNC IS REQUIRED:
 * WebReader's translation popup plays back audio using the TTS engine for the
 * TARGET language. If the reader language is still set to the source language
 * (e.g. "en") when the popup opens, WebReader tries to read the translated text
 * with an English voice and shows "cannot read aloud in this language."
 * Calling setReaderLanguage(lang) BEFORE triggering translation ensures the
 * correct TTS voice is active when the popup's play button is pressed.
 */

const WebReaderService = {
  _readyCallbacks: [],
  _isReady: false,

  /**
   * Initialize - call once on app load.
   * Listens for ReadSpeaker's rsapiReady event which fires when the
   * WebReader API is fully available.
   */
  init() {
    // rsapiReady fires when rspkr and rspkr.ui are fully initialized
    window.addEventListener('rsapiReady', () => {
      this._isReady = true;
      this._readyCallbacks.forEach((fn) => fn());
      this._readyCallbacks = [];
    });

    // If already ready (e.g. script loaded before init called), mark ready now
    if (window.rspkr?.ui) {
      this._isReady = true;
    }
  },

  /**
   * Returns true if ReadSpeaker WebReader is available and initialized.
   */
  isAvailable() {
    return !!(window.rspkr?.ui);
  },

  /**
   * Queues an action to run when WebReader is ready.
   * If already ready, runs immediately.
   */
  whenReady(fn) {
    if (this.isAvailable()) {
      fn();
    } else {
      this._readyCallbacks.push(fn);
    }
  },

  /**
   * Set the WebReader reading language.
   * This MUST be called before translation so the TTS voice matches the
   * target language. Without this, WebReader uses the source language voice
   * and shows "cannot read aloud in this language" in the translation popup.
   *
   * @param {string} lang - Language code e.g. "nl_nl", "fr_fr"
   */
  setReaderLanguage(lang) {
    if (!lang || !window.rspkr?.ui) return;

    try {
      // rspkr.ui.settings is the runtime settings object
      // Setting the language here changes which TTS voice is used for playback
      if (typeof window.rspkr.ui.settings?.set === 'function') {
        window.rspkr.ui.settings.set('general.lang', lang);
      }
      // Also set on the active player if one exists
      const player = window.rspkr.ui.getActivePlayer?.();
      if (player && typeof player.setLanguage === 'function') {
        player.setLanguage(lang);
      }
      // Fallback: set directly on rspkr config
      if (window.rspkr.cfg?.item) {
        window.rspkr.cfg.item('general.lang', lang);
      }
    } catch (e) {
      console.warn('[WebReaderService] setReaderLanguage failed:', e);
    }
  },

  /**
   * Normalize a language code to the format ReadSpeaker expects for TTS.
   * RS requires the full locale format with underscores (nl_nl, fr_fr) not
   * short codes (nl, fr) or hyphenated codes (nl-NL, fr-FR).
   * ttsSupported('nl') returns false but ttsSupported('nl_nl') returns true.
   */
  normalizeLanguageCode(lang) {
    if (!lang) return lang;

    // Map your app's locale codes to ReadSpeaker's expected format
    const localeMap = {
      // Czech
      'cs-cz': 'cs_cz', 'cs': 'cs_cz',
      // Danish
      'da-dk': 'da_dk', 'da': 'da_dk',
      // German
      'de': 'de_de', 'de-de': 'de_de', 'de-at': 'de_de',
      // English
      'en': 'en_us', 'en-us': 'en_us', 'en-gb': 'en_uk',
      // Spanish
      'es': 'es_es', 'es-es': 'es_es', 'es-ar': 'es_es', 'es-mx': 'es_es',
      // Finnish
      'fi-fi': 'fi_fi', 'fi': 'fi_fi',
      // French
      'fr': 'fr_fr', 'fr-fr': 'fr_fr', 'fr-ca': 'fr_ca',
      // Hindi
      'hi-in': 'hi_in', 'hi': 'hi_in',
      // Hmong (no RS TTS voice - will fall back gracefully)
      'hmn-us': 'hmn_us', 'hmn': 'hmn_us',
      // Hungarian
      'hu-hu': 'hu_hu', 'hu': 'hu_hu',
      // Italian
      'it': 'it_it', 'it-it': 'it_it',
      // Japanese
      'ja-jp': 'ja_jp', 'ja': 'ja_jp',
      // Korean
      'ko-kr': 'ko_kr', 'ko': 'ko_kr',
      // Norwegian
      'nb-no': 'no_nb', 'nb': 'no_nb', 'no': 'no_nb',
      // Dutch
      'nl': 'nl_nl', 'nl-nl': 'nl_nl',
      // Polish
      'pl-pl': 'pl_pl', 'pl': 'pl_pl',
      // Portuguese
      'pt': 'pt_pt', 'pt-pt': 'pt_pt', 'pt-br': 'pt_br',
      // Romanian
      'ro-ro': 'ro_ro', 'ro': 'ro_ro',
      // Russian
      'ru': 'ru_ru', 'ru-ru': 'ru_ru',
      // Slovak
      'sk-sk': 'sk_sk', 'sk': 'sk_sk',
      // Swedish
      'sv-se': 'sv_se', 'sv': 'sv_se',
      // Thai
      'th-th': 'th_th', 'th': 'th_th',
      // Vietnamese
      'vi-vn': 'vi_vn', 'vi': 'vi_vn',
      // Chinese
      'zh-cn': 'zh_cn', 'zh': 'zh_cn', 'zh-tw': 'zh_tw'
    };

    const normalized = localeMap[lang.toLowerCase()];
    if (normalized) return normalized;

    // Fallback: convert hyphen to underscore and lowercase (e.g. fr-FR → fr_fr)
    if (lang.includes('-')) return lang.replace('-', '_').toLowerCase();

    return lang.toLowerCase();
  },

  /**
   * Ensure customerid is set in the correct location.
   * ReadSpeaker reads from rspkr.c.data.params.customerid to build API URLs.
   * After a page refresh this value can be lost, causing cid=null and 401 errors.
   */
  ensureCustomerId() {
    const customerId = window.rsConf?.general?.customerid;
    if (customerId && window.rspkr?.c?.data) {
      if (!window.rspkr.c.data.params) {
        window.rspkr.c.data.params = {};
      }
      window.rspkr.c.data.params.customerid = customerId;
      return true;
    }
    return false;
  },

  /**
   * translateSelection
   *
   * WHY setReaderLanguage IS CALLED FIRST:
   * WebReader's translation popup plays back audio using the TTS engine for the
   * TARGET language. If the reader language is still set to the source language
   * (e.g. "en") when the popup opens, WebReader tries to read the translated text
   * with an English voice and shows "cannot read aloud in this language."
   * Calling setReaderLanguage(lang) BEFORE triggering translation ensures the
   * correct TTS voice is active when the popup's play button is pressed.
   *
   * WHY THE PLAY BUTTON ONLY WORKS ONCE:
   * After the player finishes reading, it reaches an "end of content" state.
   * The translate dialog stays open but the player is exhausted.
   * To replay, we must re-call translate() to re-open the dialog and re-prepare
   * the player with fresh content. We hook the play button via MutationObserver
   * to detect when the user clicks play on an already-finished player.
   *
   * @param {string} lang - Target language code e.g. "nl_nl"
   * @param {Range|null} savedRange - Previously saved DOM selection range
   */
  translateSelection(lang, savedRange) {
    if (!window.rspkr?.ui?.Tools?.Translation?.translate) {
      console.warn('[WebReaderService] Translation tool not available');
      return false;
    }

    // Normalize to full locale format - ttsSupported('nl') = false but ttsSupported('nl_nl') = true
    const normalizedLang = this.normalizeLanguageCode(lang);

    this.ensureCustomerId();
    this.setReaderLanguage(normalizedLang);

    // Step 3: Restore the selection range saved before the button click
    if (savedRange) {
      try {
        if (savedRange.startContainer?.parentNode) {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(savedRange.cloneRange());
        }
      } catch (e) {
        console.warn('[WebReaderService] Failed to restore selection range:', e);
      }
    }

    // Step 4: Call WebReader translate with the normalized language code
    try {
      window.rspkr.ui.Tools.Translation.translate(normalizedLang);
      this._hookTranslationDialogPlayButton(normalizedLang, savedRange);
      return true;
    } catch (e) {
      console.warn('[WebReaderService] Translation call failed:', e);
      try {
        window.rspkr.ui.Tools.Translation.translate(normalizedLang);
        return true;
      } catch (e2) {
        console.warn('[WebReaderService] Translation fallback also failed:', e2);
        return false;
      }
    }
  },

  /**
   * Hook the play button in the RS translation dialog.
   * After the first playback ends, clicking play would fail with "cannot read
   * aloud in this language" because the player is exhausted. We intercept the
   * play button click and re-call translate() to re-prepare the player.
   */
  _hookTranslationDialogPlayButton(lang, savedRange) {
    // Wait for the RS dialog to appear in the DOM
    const observer = new MutationObserver(() => {
      const dialog = document.querySelector('.rslightbox[data-rsmode="translate"], .rs-dialog-translate, [class*="rslightbox"]');
      if (!dialog) return;

      // Find the play button inside the dialog
      const playBtn = dialog.querySelector('.rsbtn_play, .rspopup_btn_play, [class*="play"]');
      if (!playBtn || playBtn._wrHooked) return;

      playBtn._wrHooked = true;
      observer.disconnect();

      playBtn.addEventListener('click', (e) => {
        const player = window.rspkr?.ui?.getActivePlayer?.();
        // If player is in an ended/idle state, re-trigger translation
        if (!player || player.isPlaying?.() === false) {
          e.stopImmediatePropagation();
          e.preventDefault();
          // Re-set language and re-translate to reinitialize the player
          this.setReaderLanguage(lang);
          if (savedRange) {
            try {
              if (savedRange.startContainer?.parentNode) {
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(savedRange.cloneRange());
              }
            } catch (_e) { /* ignore */ }
          }
          try {
            window.rspkr.ui.Tools.Translation.translate(lang);
          } catch (_e) {
            try { window.rspkr.ui.Tools.Translation.translate(); } catch (_e2) { /* ignore */ }
          }
        }
      }, true);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    // Clean up observer after 10 seconds if dialog never appeared
    setTimeout(() => observer.disconnect(), 10000);
  },

  /**
   * Lookup a word in the WebReader dictionary.
   *
   * @param {Range|null} savedRange - Previously saved DOM selection range
   */
  dictionaryLookup(savedRange) {
    const dictTool = window.rspkr?.ui?.Tools?.Dictionary;
    const dictFn = dictTool?.dictionary || dictTool?.lookup || dictTool?.translate || dictTool?.dictionaryLookup;

    if (typeof dictFn !== 'function') {
      console.warn('[WebReaderService] Dictionary tool not available');
      return false;
    }

    this.ensureCustomerId();

    if (savedRange) {
      try {
        if (savedRange.startContainer?.parentNode) {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(savedRange.cloneRange());
        }
      } catch (e) {
        console.warn('[WebReaderService] Failed to restore selection range:', e);
      }
    }

    try {
      dictFn('en');
      return true;
    } catch (e) {
      console.warn('[WebReaderService] Dictionary call failed:', e);
      try {
        dictFn();
        return true;
      } catch (e2) {
        console.warn('[WebReaderService] Dictionary fallback failed:', e2);
        return false;
      }
    }
  }
};

export default WebReaderService;
